import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface StaffMember {
  id: string;
  email: string | null;
  full_name: string | null;
  company: string | null;
  role: string;
  status: string;
  created_at: string;
}

export interface PermissionRow {
  role: string;
  module: string;
  allowed: boolean;
}

export interface UserPermissionRow {
  user_id: string;
  module: string;
  allowed: boolean;
}

export interface ModuleChange {
  module: string;
  label: string;
  from: string | boolean;
  to: string | boolean;
}

export interface MemberChangeMeta {
  scope?: string;
  role?: string;
  userId?: string;
  memberName?: string;
  changes?: ModuleChange[];
}

export interface MemberChange {
  id: number;
  user_id: string | null;
  actor_name: string | null;
  action: string;
  detail: string | null;
  level: string | null;
  meta: MemberChangeMeta | null;
  created_at: string;
}

export type ChangeType = "all" | "role" | "permission";

export interface ChangeRange {
  start?: string;
  end?: string;
}

interface ListResponse {
  profiles: StaffMember[];
  permissions: PermissionRow[];
  userPermissions: UserPermissionRow[];
}

async function invokeStaff<T>(payload: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("admin-staff", { body: payload });
  if (error) {
    let message = error.message || "操作失败，请稍后重试";
    try {
      const context = (error as { context?: Response }).context;
      if (context) {
        const parsed = await context.json();
        if (parsed?.error) message = parsed.error;
      }
    } catch {
      // keep original message
    }
    throw new Error(message);
  }
  const result = data as { error?: string } | null;
  if (result?.error) throw new Error(result.error);
  return data as T;
}

interface UseStaffResult {
  members: StaffMember[];
  permissions: PermissionRow[];
  userPermissions: UserPermissionRow[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  setRole: (userId: string, role: string) => Promise<void>;
  savePermissions: (permissions: { role: string; module: string; allowed: boolean }[]) => Promise<number>;
  saveUserPermissions: (
    userId: string,
    permissions: { module: string; allowed: boolean | null }[],
  ) => Promise<number>;
  loadChanges: (type: ChangeType, range?: ChangeRange) => Promise<MemberChange[]>;
}

export function useStaff(): UseStaffResult {
  const [members, setMembers] = useState<StaffMember[]>([]);
  const [permissions, setPermissions] = useState<PermissionRow[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await invokeStaff<ListResponse>({ action: "list" });
      setMembers(data.profiles ?? []);
      setPermissions(data.permissions ?? []);
      setUserPermissions(data.userPermissions ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "成员数据加载失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const setRole = useCallback(
    async (userId: string, role: string) => {
      await invokeStaff({ action: "setRole", userId, role });
      await load();
    },
    [load],
  );

  const savePermissions = useCallback(
    async (next: { role: string; module: string; allowed: boolean }[]) => {
      const result = await invokeStaff<{ updated: number }>({ action: "setPermissions", permissions: next });
      await load();
      return result.updated ?? 0;
    },
    [load],
  );

  const saveUserPermissions = useCallback(
    async (userId: string, next: { module: string; allowed: boolean | null }[]) => {
      const result = await invokeStaff<{ updated: number }>({
        action: "setUserPermissions",
        userId,
        permissions: next,
      });
      await load();
      return result.updated ?? 0;
    },
    [load],
  );

  const loadChanges = useCallback(async (type: ChangeType, range?: ChangeRange) => {
    const result = await invokeStaff<{ changes: MemberChange[] }>({
      action: "listChanges",
      type,
      start: range?.start ?? "",
      end: range?.end ?? "",
    });
    return result.changes ?? [];
  }, []);

  return {
    members,
    permissions,
    userPermissions,
    loading,
    error,
    reload: load,
    setRole,
    savePermissions,
    saveUserPermissions,
    loadChanges,
  };
}